import { readChatTurns } from "@/lib/chat-turn-history";
import { operation } from "@/lib/api-client";
import { getCsrfToken } from "@/aws/client";
import { streamChatTurn } from "@/lib/chat-turn-stream";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  History,
  Loader2,
  Paperclip,
  RefreshCw,
  Send,
} from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChatMessages, type ChatMessage } from "@/components/chat/chat-messages";
import { aws as awsClient } from "@/aws/client";
import {
  getSignedUrl,
  uploadChatFile,
  MAX_FILE_BYTES,
  MAX_FILES_PER_MESSAGE,
  type UploadedAttachment,
} from "@/lib/upload";
import { AttachmentChips, type ChipItem } from "@/components/chat/attachment-chips";
import { unwrap } from "@/lib/server-fn";
import { getDeck, restoreDeckVersion } from "@/features/decks/deck.functions";
import { DeckReviewPanel } from "@/components/decks/review-panel";
import { useEditorRestore } from "@/hooks/use-editor-restore";

export const Route = createFileRoute("/decks/$deckId")({
  component: DeckWorkspace,
  head: () => ({
    meta: [
      { title: "Deck editor — AskBell" },
      { name: "description", content: "Edit a PowerPoint deck slide by slide with the AskBell agent." },
      { property: "og:title", content: "Deck editor — AskBell" },
      { property: "og:description", content: "Edit a PowerPoint deck slide by slide with the AskBell agent." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});



type DeckInfo = {
  id: string;
  title: string;
  conversation_id: string;
  source_conversation_id?: string | null;
  current_version: number;
  current_storage_path: string;
  slide_count: number;
  source_filename: string;
};

type VersionRow = {
  version: number;
  summary: string | null;
  changed_slides: number[] | null;
  created_by: string;
  created_at: string;
};

type RenderRow = { slide_no: number; version: number; storage_path: string };

type PendingAttachment = {
  id: string;
  name: string;
  size: number;
  type: string;
  uploading: boolean;
  error?: string;
  uploaded?: UploadedAttachment;
};




function DeckWorkspace() {
  const { deckId } = Route.useParams();
  const navigate = useNavigate();

  const [deck, setDeck] = useState<DeckInfo | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [slides, setSlides] = useState<{ slide_no: number; url: string }[]>([]);
  const [selected, setSelected] = useState(1);
  const [rendering, setRendering] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const renderedOnceRef = useRef(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  /** Slides touched by the most recent agent turn: added vs edited. */
  const [slideMarks, setSlideMarks] = useState<Map<number, "added" | "edited">>(new Map());
  /** Right pane: agent chat, or the review/QA workbench. */
  const [rightTab, setRightTab] = useState<"chat" | "review">("chat");
  const [mobilePane,setMobilePane]=useState<'preview'|'workbench'>('preview');
  const { restoring, restore } = useEditorRestore();

  // Chat auto-scroll: keep the deck chat pinned to the bottom while the AI
  // is writing, unless the user has intentionally scrolled up to read.
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const stickToBottomRef = useRef(true);
  const NEAR_BOTTOM_PX = 80;

  const isNearBottom = useCallback(() => {
    const v = scrollViewportRef.current;
    if (!v) return true;
    return v.scrollHeight - v.scrollTop - v.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto", force = false) => {
    if (!force && !stickToBottomRef.current) return;
    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    scrollFrameRef.current = requestAnimationFrame(() => {
      const viewport = scrollViewportRef.current;
      if (viewport) {
        if (behavior === "smooth") {
          viewport.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
        } else {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }
      scrollFrameRef.current = null;
    });
  }, []);

  const resetChatScroll = useCallback((mode: "top" | "bottom") => {
    stickToBottomRef.current = true;
    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    scrollFrameRef.current = requestAnimationFrame(() => {
      const viewport = scrollViewportRef.current;
      if (!viewport) return;
      viewport.scrollTop = mode === "top" ? 0 : viewport.scrollHeight;
      scrollFrameRef.current = null;
    });
  }, []);

  const chips: ChipItem[] = pending.map((p) => ({
    id: p.id,
    name: p.name,
    size: p.size,
    type: p.type,
    uploading: p.uploading,
    error: p.error,
    extractionError: p.uploaded?.extraction_error ?? undefined,
  }));

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!deck || !files.length) return;
      const room = MAX_FILES_PER_MESSAGE - pending.length;
      if (room <= 0) {
        toast.error(`Max ${MAX_FILES_PER_MESSAGE} files per message.`);
        return;
      }
      const accepted = files.slice(0, room);
      const { data: { session } } = await awsClient.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return toast.error("Your session expired — sign in again.");

      for (const file of accepted) {
        const id = crypto.randomUUID();
        if (file.size > MAX_FILE_BYTES) {
          toast.error(`${file.name} is too large.`);
          continue;
        }
        setPending((prev) => [
          ...prev,
          { id, name: file.name, size: file.size, type: file.type, uploading: true },
        ]);
        try {
          const uploaded = await uploadChatFile(file, userId, deck.conversation_id);
          setPending((prev) => prev.map((p) => (p.id === id ? { ...p, uploading: false, uploaded } : p)));
        } catch (e: any) {
          setPending((prev) =>
            prev.map((p) => (p.id === id ? { ...p, uploading: false, error: e?.message ?? "Upload failed" } : p)),
          );
        }
      }
    },
    [deck, pending.length],
  );

  const loadRenders = useCallback(async (info: DeckInfo, rows: RenderRow[]) => {
    const latest = new Map<number, RenderRow>();
    for (const r of rows.filter(row => row.version === info.current_version)) {
      const prev = latest.get(r.slide_no);
      if (!prev || r.version > prev.version) latest.set(r.slide_no, r);
    }
    const list = [...latest.values()].sort((a, b) => a.slide_no - b.slide_no);
    const signed = await Promise.all(
      list.map(async (r) => ({ slide_no: r.slide_no, url: (await getSignedUrl(r.storage_path, 7200)) ?? "" })),
    );
    setSlides(signed.filter((s) => s.url));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = unwrap<{ deck: DeckInfo; versions: VersionRow[]; renders: RenderRow[] }>(
        await getDeck({ data: { deckId } }),
      );
      setDeck(res.deck);
      setVersions(res.versions ?? []);
      await loadRenders(res.deck, res.renders ?? []);
      return res;
    } catch (e: any) {
      setLoadError(e?.message ?? "Could not open this deck.");
      return null;
    }
  }, [deckId, loadRenders]);

  const renderSlides = useCallback(
    async (slideNumbers: number[] = []) => {
      setRendering(true);
      try {
        const started = await operation<{deckId:string},{jobId:string}>("deck_render_start",{deckId},getCsrfToken());
        for (let attempt=0;attempt<1200;attempt++) {
          const state=await operation<{turnId:string},{status:string;steps?:Array<{error?:string}>}>("chat_state",{turnId:started.jobId},getCsrfToken());
          if(state.status==='succeeded')break;
          if(['failed','partial','cancelled'].includes(state.status))throw new Error(state.steps?.find(s=>s.error)?.error??'The slide render did not finish.');
          if(attempt===1199)throw new Error('Slide rendering is still running. Reopen the deck to check its progress.');
          await new Promise(resolve=>setTimeout(resolve,1500));
        }
        await refresh();
      } catch (e: any) {
        toast.error(e?.message ?? "Could not render the slides.");
      } finally {
        setRendering(false);
      }
    },
    [deckId, refresh],
  );

  // Load deck + history; render the whole deck the first time it's opened.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let res = await refresh();
      if (cancelled || !res) return;
      const msgs = await awsClient
        .from("messages")
        .select("id, role, content, created_at")
        .eq("conversation_id", res.deck.conversation_id)
        .order("created_at");
      if (!cancelled && msgs.data) {
        setMessages(
          msgs.data.map((m: any) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
          })),
        );
      }
      const accepted=await readChatTurns(res.deck.conversation_id,()=>!cancelled);
      const active=accepted.turns.filter(t=>t.canManage&&['queued','running','waiting'].includes(t.status));
      if(active.length&&!cancelled){
        setIsStreaming(true);
        try{
          for(const turn of active){
            for(let attempt=0;!cancelled&&attempt<1200;attempt++){
              const state=await operation<{turnId:string},{status:string;steps?:Array<{error?:string}>}>('chat_state',{turnId:turn.turnId},getCsrfToken());
              if(['succeeded','failed','partial','cancelled'].includes(state.status)){
                if(state.status!=='succeeded')toast.error(state.steps?.find(s=>s.error)?.error??'The deck edit did not finish. Existing versions are preserved.');
                break;
              }
              await new Promise(resolve=>setTimeout(resolve,1500));
            }
          }
          if(!cancelled){
            const saved=await awsClient.from('messages').select('id,role,content,created_at').eq('conversation_id',res.deck.conversation_id).order('created_at');
            if(saved.data)setMessages(saved.data as ChatMessage[]);
            res=await refresh();
          }
        }finally{if(!cancelled)setIsStreaming(false);}
      }
      if (!cancelled && res && (res.renders ?? []).filter(r => r.version === res!.deck.current_version).length < Math.max(1,res.deck.slide_count) && !renderedOnceRef.current) {
        renderedOnceRef.current = true;
        void renderSlides([]);
      }
    })().catch(error=>{if(!cancelled)toast.error(error instanceof Error?error.message:'Could not resume the deck workspace.');});
    return () => { cancelled = true; };
  }, [refresh, renderSlides]);

  useEffect(() => {
    composerRef.current?.focus();
  }, [deckId, isStreaming]);

  // Keyboard slide navigation (PowerPoint-like arrows when not typing in a field).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName.toLowerCase();
      if (tag === "textarea" || tag === "input" || tag === "select" || target.isContentEditable) {
        return;
      }
      if (["ArrowUp", "ArrowLeft", "ArrowDown", "ArrowRight", "PageUp", "PageDown"].includes(e.key)) {
        e.preventDefault();
      }
      const max = slides.length ? Math.max(...slides.map((s) => s.slide_no)) : 0;
      if (max === 0) return;
      let next = selected;
      if (e.key === "ArrowUp" || e.key === "ArrowLeft" || e.key === "PageUp") {
        next = Math.max(1, selected - 1);
      } else if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === "PageDown") {
        next = Math.min(max, selected + 1);
      } else if (e.key === "Home") {
        next = 1;
      } else if (e.key === "End") {
        next = max;
      } else {
        return;
      }
      if (next !== selected) setSelected(next);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selected, slides]);

  // Keep the active thumbnail visible as the selection changes.
  useEffect(() => {
    const el = document.querySelector(`[data-slide-no="${selected}"]`) as HTMLElement | null;
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selected]);

  // Deck chat auto-scroll behaviors.
  useEffect(() => {
    const v = scrollViewportRef.current;
    if (!v) return;
    const onScroll = () => {
      stickToBottomRef.current = isNearBottom();
    };
    v.addEventListener("scroll", onScroll, { passive: true });
    return () => v.removeEventListener("scroll", onScroll);
  }, [isNearBottom]);

  useLayoutEffect(() => {
    scrollToBottom("auto");
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isStreaming) return;
    if (!stickToBottomRef.current) return;
    let cancelled = false;
    const pin = () => {
      if (cancelled) return;
      const v = scrollViewportRef.current;
      if (v) v.scrollTop = v.scrollHeight;
    };
    pin();
    const r1 = requestAnimationFrame(() => {
      pin();
      const r2 = requestAnimationFrame(pin);
      (pin as any)._r2 = r2;
    });
    const t = window.setTimeout(pin, 120);
    return () => {
      cancelled = true;
      cancelAnimationFrame(r1);
      window.clearTimeout(t);
    };
  }, [isStreaming, messages, scrollToBottom]);

  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => scrollToBottom("auto"));
    observer.observe(content);
    return () => {
      observer.disconnect();
      if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    };
  }, [scrollToBottom]);


  const currentSlide = useMemo(
    () => slides.find((s) => s.slide_no === selected) ?? slides[0] ?? null,
    [slides, selected],
  );

  const handleDownload = async () => {
    if (!deck) return;
    const url = await getSignedUrl(deck.current_storage_path, 3600);
    if (!url) return toast.error("Could not prepare the download.");
    const a = document.createElement("a");
    a.href = url;
    a.download = deck.source_filename;
    a.click();
  };

  const handleRestore = async (version: number) => {
    if (!deck) return;
    try {
      const done = await restore(
        deckId,
        version,
        deck.current_version,
        (request) => restoreDeckVersion({ data: { deckId, ...request } }),
        refresh,
      );
      if (done) {
        toast.success(`Restored version ${version}`);
        await renderSlides([]);
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Restore failed.");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!deck || isStreaming) return;
    const ready = pending.filter((p) => p.uploaded && !p.error);
    if (!text && !ready.length) return;
    if (pending.some((p) => p.uploading)) {
      toast.error("Wait for the files to finish uploading.");
      return;
    }
    setInput("");
    setPending([]);
    setIsStreaming(true);
    setSlideMarks(new Map());

    const now = new Date().toISOString();
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text || `Attached ${ready.length} file${ready.length === 1 ? "" : "s"}.`,
      created_at: now,
      attachments: ready.map((p) => ({
        id: p.uploaded!.id ?? p.id,
        filename: p.uploaded!.filename,
        mime_type: p.uploaded!.mime_type,
        size: p.uploaded!.size,
        storage_path: p.uploaded!.storage_path,
      })) as any,
    };
    const assistantId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      userMsg,
      { id: assistantId, role: "assistant", content: "", created_at: now },
    ]);

    const update = (fn: (m: ChatMessage) => ChatMessage) =>
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? fn(m) : m)));
    try {
      const result=await streamChatTurn({
        body:{conversationId:deck.conversation_id,clientRequestId:crypto.randomUUID(),prompt:userMsg.content,attachmentIds:ready.map(p=>p.uploaded!.id).filter((id):id is string=>Boolean(id)),modelId:'openai-direct/gpt-6-astra'},
        signal:new AbortController().signal,
        onEvent:event=>{if(event.type==='error')update(m=>({...m,error:{headline:String(event.message??'The deck edit did not finish.'),retryable:false}}));},
      });
      const saved=await awsClient.from('messages').select('id,role,content,created_at').eq('conversation_id',deck.conversation_id).order('created_at');
      if(saved.data&&result.sawDone)setMessages(saved.data as ChatMessage[]);
      const current=await refresh();
      if(current&&current.deck.current_version!==deck.current_version){
        const changed=current.versions[0]?.changed_slides??[];
        setSlideMarks(new Map(changed.map(n=>[n,'edited' as const])));
        if(changed.length)setSelected(changed[0]);
        await renderSlides([]);
      }
    } catch (e: any) {
      update((m) => ({ ...m, error: { headline: e?.message ?? "The deck agent failed.", retryable: true } }));
    } finally {
      setIsStreaming(false);
    }
  };

  if (loadError) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center text-ui-base text-muted-foreground">{loadError}</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex h-full flex-col">
        <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2.5 md:flex-nowrap">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() =>
              deck?.source_conversation_id
                ? navigate({to:"/chat",search:{conversation:deck.source_conversation_id}})
                : navigate({to:"/editors"})
            }
            aria-label={deck?.source_conversation_id ? "Back to the chat this deck came from" : "Back to editors"}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-ui-base font-bold text-foreground">{deck?.title ?? "Deck"}</h1>
            <p className="text-ui-sm text-muted-foreground">
              {deck ? `Version ${deck.current_version} · ${deck.slide_count || slides.length} slides` : "Loading…"}
            </p>
          </div>
          <div className="flex w-full justify-end gap-1 md:w-auto">
          <Button size="sm" variant="ghost" className="gap-1.5 text-ui-sm" onClick={() => renderSlides([])} disabled={rendering}>
            {rendering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-render
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="gap-1.5 text-ui-sm">
                <History className="h-3.5 w-3.5" /> Versions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-ui-sm">Version history</DropdownMenuLabel>
              {versions.map((v) => (
                <DropdownMenuItem
                  key={v.version}
                  className="flex-col items-start gap-0.5"
                  onClick={() => !restoring && v.version !== deck?.current_version && handleRestore(v.version)}
                  disabled={restoring}
                >
                  <span className="text-ui-sm font-bold">
                    v{v.version}{v.version === deck?.current_version ? " · current" : ""}
                  </span>
                  <span className="text-ui-sm text-muted-foreground line-clamp-2">{v.summary ?? "Edit"}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="secondary" className="gap-1.5 text-ui-sm" onClick={handleDownload}>
            <Download className="h-3.5 w-3.5" /> Download
          </Button>
          </div>
        </header>
        <div className="flex shrink-0 gap-1 border-b border-border p-2 md:hidden" aria-label="Deck workspace">
          {(['preview','workbench'] as const).map(pane=><Button key={pane} size="sm" variant={mobilePane===pane?'secondary':'ghost'} className="flex-1" aria-pressed={mobilePane===pane} onClick={()=>setMobilePane(pane)}>{pane==='preview'?'Slides':'Chat & review'}</Button>)}
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* Thumbnail rail */}
          <aside className={`${mobilePane==='preview'?'flex':'hidden'} w-full shrink-0 gap-2 overflow-x-auto border-b border-border bg-background/40 p-2 md:block md:w-44 md:overflow-y-auto md:border-b-0 md:border-r`}>
            {slides.length === 0 && (
              <div className="p-3 text-ui-sm text-muted-foreground">
                {rendering ? "Rendering slides…" : "No slide previews yet."}
              </div>
            )}
            {slides.map((s) => (
              <button
                key={s.slide_no}
                data-slide-no={s.slide_no}
                onClick={() => setSelected(s.slide_no)}
                className={`relative block w-28 shrink-0 overflow-hidden rounded border text-left transition md:mb-2 md:w-full ${
                  s.slide_no === (currentSlide?.slide_no ?? -1)
                    ? "border-primary ring-1 ring-primary"
                    : "border-border hover:border-muted-foreground/50"
                }`}
              >
                <img src={s.url} alt={`Slide ${s.slide_no}`} loading="lazy" className="w-full bg-ui-surface" />
                {slideMarks.get(s.slide_no) && (
                  <span
                    title={slideMarks.get(s.slide_no) === "added" ? "Added this turn" : "Edited this turn"}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-ui-success-bg text-ui-sm font-bold leading-none text-ui-success-icon shadow"
                  >
                    {slideMarks.get(s.slide_no) === "added" ? "+" : "*"}
                  </span>
                )}
                <span className="block px-1.5 py-1 text-ui-xs text-muted-foreground">Slide {s.slide_no}</span>
              </button>
            ))}

          </aside>

          {/* Selected slide */}
          <main className={`${mobilePane==='preview'?'flex':'hidden'} min-h-0 min-w-0 flex-1 flex-col overflow-auto bg-background/20 md:flex`}>
            {rightTab === "review" && (
              <div className="shrink-0 border-b border-ui-warning-icon bg-ui-warning-bg px-4 py-2 text-ui-sm text-ui-warning-icon">
                <span className="font-bold">Beta:</span> Review tools are in beta for Bellomy staff. Check findings before applying them.
              </div>
            )}
            <div className="flex min-h-0 flex-1 items-center justify-center p-3 md:p-6">
              {currentSlide ? (
                <img
                  src={currentSlide.url}
                  alt={`Slide ${currentSlide.slide_no}`}
                  className="max-h-full max-w-full rounded border border-border bg-ui-surface "
                />
              ) : (
                <div className="flex items-center gap-2 text-ui-base text-muted-foreground">
                  {rendering && <Loader2 className="h-4 w-4 animate-spin" />}
                  {rendering ? "Rendering the deck…" : "Nothing to show yet."}
                </div>
              )}
            </div>
          </main>

          {/* Agent chat + review workbench */}
          <section className={`${mobilePane==='workbench'?'flex':'hidden'} min-h-0 min-w-0 w-full flex-1 flex-col border-border md:flex md:w-[400px] md:shrink-0 md:flex-none md:border-l`}>
            <div className="flex shrink-0 border-b border-border">
              {(["chat", "review"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setRightTab(t)}
                  className={`flex-1 px-3 py-2 text-ui-sm transition ${
                    rightTab === t
                      ? "border-b-2 border-primary font-bold text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t === "chat" ? "Chat" : "Review (BETA)"}
                </button>
              ))}
            </div>
            {rightTab === "review" ? (
              deck ? <DeckReviewPanel
                deckId={deckId}
                currentVersion={deck.current_version}
                onSelectSlide={(n) => setSelected(n)}
                onApplied={(changed) => {
                  setSlideMarks(new Map(changed.map((n) => [n, "edited" as const])));
                  void renderSlides(changed);
                }}
              /> : <p role="status" className="p-4 text-ui-base text-muted-foreground">Loading deck…</p>
            ) : (
              <>
            <div ref={scrollViewportRef} className="min-h-0 flex-1 overflow-y-auto">
              <div ref={scrollContentRef}>
                {messages.length === 0 ? (
                  <div className="p-4 text-ui-base text-muted-foreground">
                    Ask for changes to this deck — for example “tighten the text on slide 4” or
                    “add a closing slide summarizing the recommendations”. Each change is saved as a new version.
                  </div>
                ) : (
                  <ChatMessages messages={messages} isStreaming={isStreaming} />
                )}
              </div>
            </div>
            <div className="border-t border-border p-3">
              <AttachmentChips
                items={chips}
                onRemove={(id) => setPending((prev) => prev.filter((p) => p.id !== id))}
              />
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    void handleFiles(Array.from(e.target.files ?? []));
                    e.target.value = "";
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 shrink-0"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isStreaming}
                  aria-label="Attach files"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Textarea
                  ref={composerRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder="Describe the change, or attach context…"
                  className="max-h-40 min-h-[60px] resize-none text-ui-base"
                  disabled={isStreaming}
                />
                <Button
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => void send()}
                  disabled={isStreaming || (!input.trim() && !pending.some((p) => p.uploaded && !p.error))}
                >
                  {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            </>
            )}
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
